import React, { useState } from "react";
import { useRequest } from "ahooks";
import { useHistory, useParams } from "react-router-dom";
import { getManufacturers, getOrderApi, updateOrderApi } from "../../../api";
import AccountLayout from "../../../layouts/AccountLayout";
import {
  ApprovalStatus,
  ChooseUploadType,
  ImageFile,
  Manufacturer,
  StatusConst,
  UserOrder,
} from "../../../interfaces";
import DownloadImages from "../DownloadImages";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { CheckGreenIcon } from "../../../assets/svgs";
import { Button, Checkbox, Loader, Textarea } from "@mantine/core";
import AdminOrderDataCard from "./AdminOrderDataCard";
import OrderAddressCard from "../../account/OrderAddressCard";
import useOrderPosition from "../../../hooks/useOrderPosition";
import AdminAttachment from "../AdminAttachment";
import ApproveDeclineAll from "./ApproveDeclineAll";
import PositionsSizeDetails from "../../AccountBulkUpload/PositionsSizeDetails";
import DeclineAllModal from "./DeclineAllModal";
import AcceptAllModal from "./AcceptAllModal";
import ViewOrderHeader from "../ViewOrderHeader";
import OrderTable from "../OrderTable";
import useOrderColumn from "../../../hooks/useOrderColumn";
import { VIEW_ORDER_COLUMNS } from "../../../constant/ViewOrderConstant";
import Notes from "./Notes";
import { useUser } from "../../../contexts";
import ActivityLogs from "../ActivityLogs";
import useSubscriber from "../../../hooks/useSubscriber";
// @ts-expect-error type exists
import { onUpdateOrderItem } from "../../../graphql/subscriptions";

const ViewOrder: React.FC = () => {
  const { id }: { id: string } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const [{ user }] = useUser();
  const { data: updatedOrderSubscribe } = useSubscriber(onUpdateOrderItem);
  const [approved, setApproved] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);
  const [approveModalVisible, setApproveModalVisible] =
    useState<boolean>(false);
  const [declineModalVisible, setDeclineModalVisible] =
    useState<boolean>(false);

  const { data, loading, mutate } = useRequest<UserOrder>(
    () => getOrderApi(id),
    {
      ready: !!id,
      onSuccess: (res) => {
        if (!res) {
          toast.error(t("orderNotFound"));
          history.push("/dashboard");
        }
      },
    }
  );
  const { data: manufacturers, loading: manufacturersLoading } = useRequest<
    Manufacturer[]
  >(() => getManufacturers());

  const { positionImageAvailable, declineAllImage, approveAllImage } =
    useOrderPosition(data);

  const onApproveModalOpen = () => {
    setApproveModalVisible(true);
  };

  const onApproveModalClose = () => {
    setApproveModalVisible(false);
  };

  const onDeclineModalOpen = () => {
    setDeclineModalVisible(true);
  };

  const onDeclineModalClose = () => {
    setDeclineModalVisible(false);
  };

  const onDeclineAllImages = async (
    rejectedDescription: string,
    rejectedReason: string,
    files: ImageFile[]
  ) => {
    setDeclineModalVisible(false);
    const updatedOrder = await declineAllImage(
      rejectedDescription,
      rejectedReason,
      files
    );
    if (updatedOrder) {
      mutate(updatedOrder);
    }
    toast.success(t("declineAllPrintData"));
  };

  const onApproveAllImages = async (acceptedDescription: string) => {
    setApproveModalVisible(false);
    const updatedOrder = await approveAllImage(acceptedDescription);
    if (updatedOrder) {
      mutate(updatedOrder);
    }
    toast.success(t("acceptAllPrintData"));
  };

  const updateOrder = useRequest(updateOrderApi, {
    manual: true,
  });

  const sendPrintDataProduction = async () => {
    if (!data) return;

    for (let i = 0; i < data.positions.length; i++) {
      const position = data.positions[i];
      if (
        position?.productGroup &&
        position.positionImage?.find(
          (image) => image.approvalStatus === ApprovalStatus.No
        )
      ) {
        toast.error(t("positionsNotCompleted"));
        return;
      }
    }

    await updateOrder.run({
      id,
      status: StatusConst.Production,
      uploadedDate: new Date().toISOString(),
    });
    setTimeout(() => setSent(true), 300);
  };

  const { filterColumns } = useOrderColumn({
    orders: [data as UserOrder],
    orderFilter: null,
    manufacturers: manufacturers ?? [],
    onUpdateOrder: (order) => mutate(order),
  });

  const handleOrderRefresh = (order: Partial<UserOrder> & { id: string }) => {
    if (order.id !== data?.id || order.chooseUpload === data.chooseUpload)
      return;
    if (order.chooseUpload === ChooseUploadType.account) {
      history.push(`/dashboard/order/${order.id}`);
    }
  };

  React.useEffect(() => {
    if (!user) return;
    updateOrder.run({
      id,
      lastOpened: user.email,
    });
  }, [user]);

  React.useEffect(() => {
    if (
      !updatedOrderSubscribe ||
      (updatedOrderSubscribe &&
        !(updatedOrderSubscribe as Record<"onUpdateOrderItem", UserOrder>)
          .onUpdateOrderItem)
    )
      return;

    handleOrderRefresh(
      (updatedOrderSubscribe as Record<"onUpdateOrderItem", UserOrder>)
        .onUpdateOrderItem
    );
  }, [updatedOrderSubscribe]);

  return (
    <AccountLayout account>
      {!sent ? (
        <>
          <ViewOrderHeader orderId={id} />
          {!loading && data && !manufacturersLoading ? (
            <>
              <div className="grid gap-30 print-data-upload-admin pt-20">
                <div>
                  <OrderAddressCard
                    isShippingAddressEditable={true}
                    isBillingAddressEditable={true}
                    data={data}
                    onUpdateOrder={(order) => mutate(order)}
                  />
                </div>
                <Notes order={data} mutate={mutate} />
              </div>

              <div className="py-40 pl-8 pr-8">
                <OrderTable
                  isPagination={false}
                  columns={filterColumns(VIEW_ORDER_COLUMNS)}
                  data={[data]}
                  nextToken={null}
                  loadOrders={() => {}}
                  fetchLoading={loading}
                />
              </div>
              <div className="grid print-data-upload-log   ml-0">
                <div>
                  {positionImageAvailable(data) ? (
                    <p className="f-28 my-20 f-weight-7">{t("uploadData")}</p>
                  ) : (
                    <p className="section-heading my-20  ">
                      {t("UploadedDataWarning")}
                    </p>
                  )}

                  {data?.positions?.map((position, index) => (
                    <AdminOrderDataCard
                      key={index}
                      order={data}
                      item={position}
                      index={index}
                      mutate={mutate}
                    />
                  ))}
                  {positionImageAvailable(data) && (
                    <div>
                      <ApproveDeclineAll
                        onApproveModalOpen={onApproveModalOpen}
                        onDeclineModalOpen={onDeclineModalOpen}
                      />

                      <div className="mb-70">
                        <DownloadImages positions={data?.positions} />
                      </div>

                      <div className="mb-30 f-28 f-weight-7">
                        {t("deliveryNote")}
                      </div>

                      <AdminAttachment data={data} files={data?.files || []} />
                    </div>
                  )}
                </div>
                <div className="row row-center">
                  <PositionsSizeDetails positions={data.positions} />
                </div>
                <ActivityLogs orderId={data.id} />
              </div>
              <p className="section-heading mt-70">{t("furtherInformation")}</p>
              <Checkbox
                radius={5}
                disabled
                label={t("furtherInfoFlowingMotifs")}
                className="my-10"
                checked={data.furtherInfoFlowingMotifs ?? false}
              />
              <Checkbox
                radius={5}
                disabled
                label={t("furtherInfoQualityCheck")}
                className="mb-20"
                checked={data.furtherInfoQualityCheck ?? false}
              />
              <div className="w-sx">
                <Textarea
                  readOnly
                  className="mt-10 mb-30"
                  minRows={4}
                  defaultValue={data.furtherInfo}
                ></Textarea>
              </div>
              <div className=" row  row-column row-v-center  px-10 mt-70">
                <Checkbox
                  checked={approved}
                  onChange={(e: any) => setApproved(e.target.checked)}
                  label={t("approveText")}
                />
                <Button
                  mt={10}
                  px={100}
                  size="lg"
                  loading={updateOrder.loading}
                  disabled={!approved || updateOrder.loading}
                  onClick={() => sendPrintDataProduction()}
                >
                  {t("sendToProduction")}
                </Button>
              </div>
            </>
          ) : (
            <div className="row row-center mt-30">
              <Loader />
            </div>
          )}
        </>
      ) : (
        <div className="sent-printdata">
          <CheckGreenIcon />

          <span>{t("sendSuccessProduction")}</span>
        </div>
      )}

      {declineModalVisible && (
        <DeclineAllModal
          modalVisible={declineModalVisible}
          setModalVisible={setDeclineModalVisible}
          onDanger={onDeclineModalClose}
          onSuccess={(
            rejectedDescription: string,
            rejectedReason: string,
            files: ImageFile[]
          ) => onDeclineAllImages(rejectedDescription, rejectedReason, files)}
          title={"declineAllPrintDataWarning"}
          contentStyle={"pb-16"}
        />
      )}

      {approveModalVisible && (
        <AcceptAllModal
          modalVisible={approveModalVisible}
          setModalVisible={setApproveModalVisible}
          onDanger={onApproveModalClose}
          onSuccess={(acceptedDescription: string) =>
            onApproveAllImages(acceptedDescription)
          }
          title={"acceptAllPrintDataWarning"}
          contentStyle={"pb-16"}
        />
      )}
    </AccountLayout>
  );
};

export default ViewOrder;
